import styles from './Navigation.module.scss';

import React, { useMemo } from 'react';

import { DirectionalHint, TooltipHost, Icon, Stack } from '@fluentui/react';

import { AppLogo } from './AppLogo';
import { ROUTES } from '../../config/routes';
import { RootState } from '../../redux/types';
import { useDispatch, useSelector } from 'react-redux';
import { css } from '../../util/Css';
import { getSlug } from '../../util/String';
import { history } from '../../redux/store';
import { useOktaAuth } from '@okta/okta-react';
import { BadgeEnvironment } from './Badge';
import { setExpandNav } from '../../redux/ui/actions';
import { useCallback } from 'react';
import { BuildInfo } from './Page';
import { useLocation } from 'react-router';
import { isOpsManager, logout } from '../../okta';

declare const healConfig: any;

type NavigationProps = {
  buildInfo?: BuildInfo;
};

interface NavDefinition {
  name?: string;
  url?: string;
  icon?: string;
  exact?: boolean;
}

export const Navigation = ({ buildInfo }: NavigationProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { authState } = useOktaAuth();

  const ui = useSelector((state: RootState) => state.ui);
  const expand = ui.expandNav;

  const navItems = useMemo(() => {
    const items: NavDefinition[] = [
      { name: 'Search', url: ROUTES.search, icon: 'Search' },
      { name: 'Create Visit', url: ROUTES.createVisit, icon: 'Add' },
      { name: 'Analytics', url: ROUTES.dashboard, icon: 'ViewDashboard' },
      {},
      { name: 'Visits', url: ROUTES.visits, icon: 'Car' },
      { name: 'Visits Map', url: ROUTES.visitsMap, icon: 'Nav2DMapView', exact: true },
      { name: 'Visits Timeline', url: ROUTES.visitsTimeline, icon: 'TimelineProgress', exact: true },
      { name: 'Provider Visits', url: ROUTES.visitsProvider, icon: 'AllAppsMirrored', exact: true },
      {},
      { name: 'Providers', url: ROUTES.providers, icon: 'Medical' },
      { name: 'Patients', url: ROUTES.patients, icon: 'Contact' },
      { name: 'Partners', url: ROUTES.concierge, icon: 'SecurityGroup' },
      { name: 'Pods', url: ROUTES.pods, icon: 'WebAppBuilderModule' },
      {},
      { name: 'Markets', url: ROUTES.markets, icon: 'VennDiagram' },
      { name: 'Markets Map', url: ROUTES.marketMap, icon: 'MapPin', exact: true },
      { name: 'Scheduling', url: ROUTES.scheduling, icon: 'Calendar' },
      {},
      { name: 'Eligibility Checker', url: ROUTES.toolInsurance, icon: 'ContactCard' },
      { name: 'Claims', url: ROUTES.claims, icon: 'FabricReportLibrary' },
    ];

    if (authState?.accessToken && isOpsManager(authState.accessToken)) {
      items.push({});
      items.push({ name: 'Insurance Rules', url: ROUTES.insuranceRules, icon: 'FlowChart' });
      items.push({ name: 'Admin Tools', url: ROUTES.toolAdmin, icon: 'Admin' });
    }

    return items;
  }, [authState?.accessToken]);

  const isSelected = useCallback(
    (item: NavDefinition) => {
      const activeUrl = location.pathname === '/' ? ROUTES.visits : location.pathname;
      const exactNavItem = navItems.find((i) => i.exact && activeUrl === i.url);
      if (item === exactNavItem) {
        return true;
      }
      if (exactNavItem) {
        return false;
      }

      return activeUrl === item.url || (!item.exact && activeUrl.startsWith(item.url!));
    },
    [navItems, location.pathname]
  );

  const toggle = useCallback(() => {
    dispatch(setExpandNav(!ui.expandNav));
  }, [dispatch, ui.expandNav]);

  return (
    <div
      className={css(styles.commands, 'flex flex-column h-scroll-hidden', {
        expanded: !!expand,
      })}
    >
      <Stack root={{ style: { marginLeft: 6, marginTop: 6 } }} horizontal verticalAlign="center">
        <AppLogo />
        {expand && <span className="ms-font-xl">&nbsp;Operations</span>}
      </Stack>
      {expand && (
        <div style={{ position: 'absolute', right: 6, top: 6 }}>
          <BadgeEnvironment />
        </div>
      )}

      <div className="flex-grow flex-column v-scroll-auto h-scroll-hidden">
        <Separator />

        {navItems.map((item, index) =>
          item.name ? <NavItem key={index} selected={isSelected(item)} expanded={expand} testId={`btn_nav_${getSlug(item.name)}`} {...item} /> : <Separator key={index} />
        )}
      </div>

      {expand && (
        <>
          <div className={styles.shortcuts}>
            <kbd>?</kbd> - keyboard shortcuts
          </div>
          {buildInfo && <span className={styles.version}>{`${healConfig.env.toUpperCase()} v${buildInfo.version}b${buildInfo.buildNumber}`}</span>}
          <Separator />
        </>
      )}

      <NavItem
        expanded={expand}
        name={expand ? 'Collapse' : 'Expand'}
        action={toggle}
        icon={expand ? 'DoubleChevronLeft' : 'DoubleChevronRight'}
        testId={expand ? 'btn_nav_collapse' : 'btn_nav_expand'}
      />
      <NavItem expanded={expand} name="Sign Out" action={logout} icon="PowerButton" testId="btn_nav_sign-out" />
    </div>
  );
};

interface NavItemProps {
  name?: string;
  icon?: string;
  url?: string;
  action?: () => void;
  className?: string;
  selected?: boolean;
  expanded?: boolean;
  testId?: string;
}

const NavItem = (props: NavItemProps) => {
  const onClick = (e: React.MouseEvent) => {
    if (props.url) {
      if (e.metaKey || e.ctrlKey) {
        window.open(props.url, '_blank');
      } else {
        history.push(props.url);
      }
    } else if (props.action) {
      props.action();
    }
  };

  const content = (
    <div className={css(styles.navItem, props.className, { [styles.selected]: !!props.selected })}>
      <button style={{ outline: 'none', width: 200 }} className={css(styles.anchor, 'link-button flex flex-center')} onClick={onClick} data-tid={props.testId}>
        <Icon className={styles.icon} iconName={props.icon} />
        {props.expanded && <div className={styles.text}>{props.name}</div>}
      </button>
    </div>
  );

  if (props.expanded) {
    return content;
  }

  return (
    <TooltipHost content={props.name} calloutProps={{ directionalHint: DirectionalHint.rightCenter, isBeakVisible: false }}>
      {content}
    </TooltipHost>
  );
};

const Separator = () => {
  return (
    <div className={css(styles.separator, 'flex')}>
      <div className={css(styles.item, 'flex-cell')} />
    </div>
  );
};
